import { CURRENCIES, EXEMPTION_CODES, KINDS, PAYMENT_TYPES, calcTotals, applyKind, money } from "./invoiceDefaults";

export const inputCls = "w-full h-8 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500 transition-shadow";

export function Field({ label, children, hint, className = "" }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
    </label>
  );
}

export function TextIn({ f, set, k, type = "text", testId, ...rest }) {
  return <input data-testid={testId || `inv-${k}`} type={type} className={inputCls} value={f[k] ?? ""}
    onChange={(e) => set({ ...f, [k]: e.target.value })} {...rest} />;
}

export function SelectIn({ f, set, k, options, testId, onPick }) {
  return (
    <select data-testid={testId || `inv-${k}`} className={inputCls} value={f[k] ?? ""}
      onChange={(e) => (onPick ? onPick(e.target.value) : set({ ...f, [k]: e.target.value }))}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function Toggle({ on, onClick, testId, yes = "Evet", no = "Hayır" }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`h-8 rounded-md border text-[12px] font-semibold transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-300 text-slate-600"}`}>
      {on ? yes : no}
    </button>
  );
}

export function Section({ title, children, right }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-[11px] font-bold uppercase tracking-[0.14em] text-indigo-700">{title}</h4>
        {right}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2">{children}</div>
    </section>
  );
}

export function CustomerSection({ f, set, countryInfo, foreignTax = "22222222222" }) {
  const toggleAbroad = () => {
    const is_abroad = !f.is_abroad;
    const auto = ["", "2222222222", "22222222222", "11111111111", foreignTax].includes(f.tax_number);
    set({ ...f, is_abroad, tax_number: auto ? (is_abroad ? foreignTax : "11111111111") : f.tax_number,
      country: !is_abroad && !f.country ? "Türkiye" : f.country });
  };
  return (
    <Section title="Müşteri">
      {countryInfo && (
        <div data-testid="inv-country-info" className={`col-span-2 md:col-span-4 rounded-md px-2 py-1 text-[12px] ${countryInfo.warning ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-emerald-50 border border-emerald-200 text-emerald-800"}`}>
          Ülke tespiti: <b>{countryInfo.source}</b>{countryInfo.country ? ` → ${countryInfo.country}` : ""} · {countryInfo.is_abroad ? "yabancı" : "yerli"}
          {countryInfo.warning && <> · {countryInfo.warning}</>}
        </div>
      )}
      <Field label="Ad Soyad / Unvan" className="col-span-2"><TextIn f={f} set={set} k="customer_name" /></Field>
      <Field label="Tip"><SelectIn f={f} set={set} k="contact_type" options={[{ value: "person", label: "Şahıs" }, { value: "company", label: "Şirket" }]} /></Field>
      <Field label="Yurt dışı">
        <Toggle testId="inv-is_abroad" on={f.is_abroad} onClick={toggleAbroad} yes="Evet · yabancı" no="Hayır · yerli" />
      </Field>
      <Field label="VKN / TCKN" hint="Yabancı: 22222222222 · TCKN bilinmeyen yerli: 11111111111"><TextIn f={f} set={set} k="tax_number" /></Field>
      <Field label="Vergi dairesi"><TextIn f={f} set={set} k="tax_office" /></Field>
      <Field label="E-posta"><TextIn f={f} set={set} k="customer_email" /></Field>
      <Field label="Telefon"><TextIn f={f} set={set} k="customer_phone" /></Field>
      <Field label="Ülke"><TextIn f={f} set={set} k="country" /></Field>
      <Field label="Şehir"><TextIn f={f} set={set} k="city" /></Field>
      <Field label="Cari kod öneki" hint="Yeni cariye 120.G001 gibi sıralı kod"><TextIn f={f} set={set} k="contact_prefix" /></Field>
      <Field label="Adres"><TextIn f={f} set={set} k="address" /></Field>
    </Section>
  );
}

export function AmountSection({ f, set, rateDate, rateSource, rates }) {
  const pickCurrency = (c) => set({ ...f, currency: c, exchange_rate: c === "TRL" ? 1 : (rates?.[c] || "") });
  const rateHint = f.currency === "TRL" ? "TL faturada kur yok"
    : !f.exchange_rate ? "Kur alınamadı, elle girin"
    : `${rateSource || "TCMB döviz alış"}${rateDate ? ` · ${rateDate}` : ""} · değiştirilebilir`;
  return (
    <Section title="Fatura ve Döviz">
      <Field label="Fatura türü">
        <SelectIn f={f} set={set} k="kind" options={KINDS} onPick={(v) => set(applyKind(f, v))} />
      </Field>
      <Field label="Fatura tarihi"><TextIn f={f} set={set} k="issue_date" type="date" /></Field>
      <Field label="Para birimi">
        <SelectIn f={f} set={set} k="currency" options={CURRENCIES} onPick={pickCurrency} />
      </Field>
      <Field label="Döviz kuru (₺)" hint={rateHint}>
        <TextIn f={f} set={set} k="exchange_rate" type="number" step="0.0001" disabled={f.currency === "TRL"} />
      </Field>
      <Field label="Fatura açıklaması" className="col-span-2 md:col-span-4"><TextIn f={f} set={set} k="description" /></Field>
    </Section>
  );
}

export function TaxSection({ f, set }) {
  const needReason = ["250", "350"].includes(f.vat_exemption_reason_code);
  const hasZero = (f.lines || []).some((l) => Number(l.vat_rate) === 0);
  return (
    <Section title="İstisna / Muafiyet / Tevkifat">
      <Field label="Muafiyet / istisna kodu" hint={hasZero ? "KDV %0 satır var: zorunlu" : "KDV %0 satır yoksa boş bırakılabilir"} className="col-span-2">
        <input list="inv-exemption-codes" data-testid="inv-vat_exemption_reason_code" className={inputCls}
          value={f.vat_exemption_reason_code} onChange={(e) => set({ ...f, vat_exemption_reason_code: e.target.value.split(" ")[0] })}
          placeholder="Listeden seçin veya kodu yazın" />
        <datalist id="inv-exemption-codes">{EXEMPTION_CODES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}</datalist>
      </Field>
      <Field label={`Muafiyet açıklaması${needReason ? " *" : ""}`} className="col-span-2"><TextIn f={f} set={set} k="vat_exemption_reason" /></Field>
      {f.kind === "tevkifat" && (<>
        <Field label="Tevkifat oranı (%)" hint="Ör. 20, 50, 70, 90"><TextIn f={f} set={set} k="vat_withholding_rate" type="number" /></Field>
        <Field label="Tevkifat kodu" hint="GİB kodu, ör. 6xx"><TextIn f={f} set={set} k="vat_withholding_code" /></Field>
      </>)}
    </Section>
  );
}

export function EDocSection({ f, set, accounts }) {
  return (
    <Section title="e-Belge ve Ödeme">
      <Field label="İnternet satışı">
        <Toggle testId="inv-internet_sale" on={f.internet_sale} onClick={() => set({ ...f, internet_sale: !f.internet_sale })} />
      </Field>
      <Field label="Satış sitesi"><TextIn f={f} set={set} k="sale_url" disabled={!f.internet_sale} /></Field>
      <Field label="Ödeme tipi"><SelectIn f={f} set={set} k="payment_type" options={PAYMENT_TYPES} /></Field>
      <Field label="Ödeme platformu"><TextIn f={f} set={set} k="payment_platform" /></Field>
      <Field label="Sipariş tarihi (ödeme tarihi = fatura +45 gün)"><TextIn f={f} set={set} k="payment_date" type="date" /></Field>
      <Field label="Sipariş no"><TextIn f={f} set={set} k="order_no" /></Field>
      <Field label="e-Fatura senaryo">
        <SelectIn f={f} set={set} k="einvoice_scenario" options={[{ value: "basic", label: "Temel" }, { value: "commercial", label: "Ticari" }]} />
      </Field>
      <Field label="Tahsil edildi (kasa)">
        <select data-testid="inv-payment_account_id" className={inputCls} value={f.mark_paid ? f.payment_account_id : ""}
          onChange={(e) => set({ ...f, mark_paid: !!e.target.value, payment_account_id: e.target.value })}>
          <option value="">Hayır</option>
          {(accounts || []).map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
        </select>
      </Field>
      <Field label="Fatura notu" className="col-span-2 md:col-span-4"><TextIn f={f} set={set} k="invoice_note" /></Field>
    </Section>
  );
}

export function TotalsBar({ f }) {
  const t = calcTotals(f);
  const cell = (label, v, strong, testId) => (
    <div key={label} className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-indigo-200">{label}</span>
      <span data-testid={testId} className={`tabular-nums ${strong ? "text-lg font-bold text-white" : "text-sm font-semibold text-indigo-50"}`}>{v}</span>
    </div>
  );
  return (
    <div data-testid="inv-totals" className="flex flex-wrap items-end gap-x-6 gap-y-2 rounded-xl bg-indigo-950 px-4 py-3">
      {cell("KDV hariç", money(t.net, f.currency), false, "inv-total-net")}
      {Object.entries(t.byVat).filter(([v]) => Number(v) > 0).map(([v, amt]) => cell(`KDV %${v}`, money(amt, f.currency), false, `inv-total-vat-${v}`))}
      {cell("Genel toplam", money(t.total, f.currency), true, "inv-total-gross")}
      {f.currency !== "TRL" && cell("TL karşılığı", money(t.tl, "TRL"), false, "inv-total-tl")}
    </div>
  );
}
